/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import auth from '@/auth/authService'

import firebase from 'firebase/app'
import 'firebase/auth'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [

    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      meta: {
        requiresAuth: true
      },
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/report/all'
        },

        // =============================================================================
        // USER LAYOUT ROUTES
        // =============================================================================
        {
          path: "/user/add",
          name: "add-user",
          component: () => import("./views/pages/user/AddUser.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        {
          path: "/user/all",
          name: "user-all",
          component: () => import("./views/pages/user/Users.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },

        {
          path: "/user/:user_id",
          name: "user",
          component: () => import("./views/pages/user/User.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },

        {
          path: "/user/edit/:user_id",
          name: "user-edit",
          component: () => import("./views/pages/user/EditUser.vue"),
          meta: {
            rule: "user",
            requiresAuth: true
          }
        },

        // =============================================================================
        // REPORT LAYOUT ROUTES
        // =============================================================================
        {
          path: "/report/add",
          name: "add-report",
          component: () => import("./views/pages/report/AddReport.vue"),
          meta: {
            rule: "user",
            requiresAuth: true
          }
        },
        {
          path: "/report/all",
          name: "report-all",
          component: () => import("./views/pages/report/Reports.vue"),
          meta: {
            rule: "user",
            requiresAuth: true
          }
        },
        {
            path: "/report/daily/edit/:report_id",
            name: "daily-report-edit",
            component: () => import("./views/pages/report/DailyReportEdit.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/daily/add",
            name: "daily-report-add",
            component: () => import("./views/pages/report/AddDailyReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/daily",
            name: "daily-report",
            component: () => import("./views/pages/report/DailyReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/dailyCashier/edit/:report_id",
            name: "daily-cashier-report-edit",
            component: () => import("./views/pages/report/DailyCashierReportEdit.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/dailyCashier/add",
            name: "daily-cashier-report-add",
            component: () => import("./views/pages/report/AddDailyCashierReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/dailyCashier",
            name: "daily-cashier-report",
            component: () => import("./views/pages/report/DailyCashierReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/workHourDiary/edit/:report_id",
            name: "work-hour-diary-edit",
            component: () => import("./views/pages/report/WorkHourDiaryEdit.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/workHourDiary/add",
            name: "work-hour-diary-add",
            component: () => import("./views/pages/report/AddWorkHourDiary.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/workHourDiary",
            name: "work-hour-diary",
            component: () => import("./views/pages/report/WorkHourDiary.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/automaticCashRegister/edit/:report_id",
            name: "automatic-cash-register-report-edit",
            component: () => import("./views/pages/report/AutomaticCashRegisterReportEdit.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/automaticCashRegister/add",
            name: "automatic-cash-register-report-add",
            component: () => import("./views/pages/report/AddAutomaticCashRegisterReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
            path: "/report/automaticCashRegister",
            name: "automatic-cash-register-report",
            component: () => import("./views/pages/report/AutomaticCashRegisterReport.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
        },
        {
          path: "/report/:report_id",
          name: "report",
          component: () => import("./views/pages/report/Report.vue"),
          meta: {
            rule: "user",
            requiresAuth: true
          }
        },
        {
          path: "/report/edit/:report_id",
          name: "report-edit",
          component: () => import("./views/pages/report/EditReport.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        // =============================================================================
        // SHIFT LAYOUT ROUTES
        // =============================================================================
        {
          path: "/shift/add",
          name: "add-shift",
          component: () => import("./views/pages/shift/AddShift.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        {
          path: "/shift/all",
          name: "shift-all",
          component: () => import("./views/pages/shift/Shifts.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        {
          path: "/shift/:shift_id",
          name: "shift",
          component: () => import("./views/pages/shift/Shift.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        {
          path: "/shift/edit/:shift_id",
          name: "shift-edit",
          component: () => import("./views/pages/shift/EditShift.vue"),
          meta: {
            rule: "admin",
            requiresAuth: true
          }
        },
        {
            path: "/work-hours/add",
            name: "add-work-hours",
            component: () => import("./views/pages/shift/AddWorkHours.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
          },
        {
            path: "/work-hours/all",
            name: "work-hours-all",
            component: () => import("./views/pages/shift/WorkHours.vue"),
            meta: {
              rule: "user",
              requiresAuth: true
            }
          },
          {
            path: "/work-hours/edit/:work_hour_id",
            name: "work-hours-edit",
            component: () => import("./views/pages/shift/EditWorkHours.vue"),
            meta: {
              rule: "admin",
              requiresAuth: true
            }
          },

        // =============================================================================
        // HOLIDAY PLAN LAYOUT ROUTES
        // =============================================================================
        {
            path: "/monthly-working-hours/edit/:monthly_working_hours_id",
            name: "edit-monthly-working-hours",
            component: () => import("./views/pages/shift/EditMonthlyWorkingHours.vue"),
            meta: {
                rule: "admin",
                requiresAuth: true
            }
        },
        {
            path: "/monthly-working-hours/add",
            name: "add-monthly-working-hours",
            component: () => import("./views/pages/shift/AddMonthlyWorkingHours.vue"),
            meta: {
                rule: "admin",
                requiresAuth: true
            }
        },
        {
            path: "/monthly-working-hours/all",
            name: "monthly-working-hours-all",
            component: () => import("./views/pages/shift/MonthlyWorkingHours.vue"),
            meta: {
                rule: "admin",
                requiresAuth: true
            }
        }, 

        // =============================================================================
        // HOLIDAY PLAN LAYOUT ROUTES
        // =============================================================================
        {
            path: "/holiday-plan/add",
            name: "add-holiday-plan",
            component: () => import("./views/pages/holiday-plan/AddHolidayPlan.vue"),
            meta: {
                rule: "user",
                requiresAuth: true
            }
        },
        {
            path: "/holiday-plan/all",
            name: "holiday-plan-all",
            component: () => import("./views/pages/holiday-plan/HolidayPlan.vue"),
            meta: {
                rule: "user",
                requiresAuth: true
            }
        },  
        {
            path: "/holiday-plan/edit/:holiday_plan_id",
            name: "holiday-plan-edit",
            component: () => import("./views/pages/holiday-plan/EditHolidayPlan.vue"),
            meta: {
              rule: "admin",
              requiresAuth: true
            }
          },

        // =============================================================================
        // HOLIDAY LAYOUT ROUTES
        // =============================================================================
        {
            path: "/holiday/add",
            name: "add-holiday",
            component: () => import("./views/pages/holiday/AddHoliday.vue"),
            meta: {
                rule: "admin",
                requiresAuth: true
            }
        },
        {
            path: "/holiday/all",
            name: "holiday-all",
            component: () => import("./views/pages/holiday/Holidays.vue"),
            meta: {
                rule: "admin",
                requiresAuth: true
            }
        },
      ]
  },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/callback',
          name: 'auth-callback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/register',
          name: 'page-register',
          component: () => import('@/views/pages/register/Register.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/reset-password',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/error',
          name: 'page-error',
          component: () => import('@/views/pages/Error.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'editor'
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})


router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

// this function runs on every request
// if route has meta atribute requiresAuth: true
// user must be logged in, if he's not, it will redirect him to /pages/login
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // meta is field in router element, where you can define user role
    // and if auth is neccesary
    if (to.meta.requiresAuth) {
      if (!auth.isAuthenticated()) {
        router.push({ path: "/pages/login", query: { to: to.path } });
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;